import {
  getOrdersClient,
  makeAuthenticatedQuery,
} from '@b-stock/fe-core/bstock-next/queries'
import { buildAxiosRequestConfig } from '@b-stock/fe-core/bstock-react'
import { OrdersApiControllerFindAllSubjectTypeEnum } from '@b-stock/order-api-client'

/**
 * The orders endpoint has no `sellerCollects` filter, so the check is a scan
 * of the buyer's most recent orders. A buyer whose only Seller Collects order
 * is older than this window reads as first-time — they see an explainer they
 * did not need, which is the harmless direction to be wrong in.
 */
const PRIOR_ORDER_LOOKUP_LIMIT = 100

/**
 * Which Seller Collects orders this buyer has with each seller, within the
 * lookup window.
 *
 * Keyed by seller rather than totalled, because both surfaces that ask are
 * asking about *this* seller: payment and shipping instructions differ per
 * storefront, so a buyer on their first Journeys order needs the explainer
 * even if they have ordered from AT&T ten times.
 *
 * Order ids rather than a tally, because the order page has to ask whether
 * *this* order is the buyer's only one with the seller. A tally cannot answer
 * that from a result fetched before the order existed: one prior order and
 * one brand-new order both read as "1". The ids let the caller take the order
 * it is looking at out of the reckoning, so the answer holds whether or not
 * the scan has caught up with a purchase made moments ago.
 *
 * The grouping is free — the scan already holds the orders, and the endpoint
 * could not have answered per-seller anyway.
 */
export type SellerCollectsOrderIds = Record<string, string[]>

const getSellerCollectsOrderIds = async (
  accessToken: string
): Promise<SellerCollectsOrderIds> => {
  const ordersClient = getOrdersClient()

  const { data } = await ordersClient.ordersApiControllerFindAll(
    {
      limit: PRIOR_ORDER_LOOKUP_LIMIT,
      orderType: ['LISTING'],
      subjectType: [OrdersApiControllerFindAllSubjectTypeEnum.Buyer],
    },
    buildAxiosRequestConfig({ accessToken })
  )

  const orderIds: SellerCollectsOrderIds = {}

  for (const order of data.orders) {
    if (order.listing?.sellerCollects !== true) continue

    // Keyed by the seller's account: the only identifier this endpoint and
    // the listing/order shapes the callers hold agree on. The order's own
    // `listing` carries no seller at all, and the listing's `seller.account`
    // carries no id, so `accountId` on each side is the one bridge. An order
    // with no seller cannot be attributed and is left out rather than pooled
    // under a placeholder key — pooling would make two unattributable orders
    // look like a repeat purchase from one seller.
    const sellerAccountId = order.seller.accountId
    if (!sellerAccountId) continue

    orderIds[sellerAccountId] = [
      ...(orderIds[sellerAccountId] ?? []),
      order._id,
    ]
  }

  return orderIds
}

/**
 * Seller Collects order ids per seller for this buyer.
 *
 * A map rather than a boolean because two surfaces ask different questions of
 * the same answer, and neither should cost a second request: the pre-bid
 * banner asks whether there are any with this seller, the first-win modal asks
 * whether this order is the only one with them.
 */
const sellerCollectsOrderIdsQuery = makeAuthenticatedQuery(
  'sellerCollectsOrderIds',
  getSellerCollectsOrderIds
)

export default sellerCollectsOrderIdsQuery
